/**
 * One WebSocket for the whole app, carrying price changes for the games on screen.
 *
 * Opened only when the user granted `stream:realtime`; otherwise the board polls. With
 * `stream:ticks` as well it subscribes to the low-latency `ticks` channel (whole venue quotes,
 * sent the moment a price moves); without it, to `liquidity` (price/size deltas). A key that is
 * refused `ticks` drops back to `liquidity` on the same socket.
 *
 * The API treats each subscribe as a REPLACE of the channel's filter, so we always send the full
 * set. Both channels are changes-only and never replay, so after a reconnect — or a gap in a
 * game's tick `seq` — we re-read over REST.
 */
import { useEffect, useSyncExternalStore } from "react";
import { STREAM_URL } from "./api";
import { applyChanges, applyTicks, type LiquidityChange, type Tick } from "./board";
import { getApiKey, hasScope, identity, STREAM_SCOPE, subscribeAuth, TICKS_SCOPE } from "./omauth";
import { queryClient } from "./queryClient";
import type { Position } from "./types";

export type FeedStatus = "live" | "connecting" | "off";
type Channel = "ticks" | "liquidity";

/** Server cap per connection. */
const MAX_CONTESTS = 200;
/** At most one REST re-read per game per window, however chatty the stream gets. */
const REFETCH_WINDOW_MS = 10_000;

/* ------------------------------------------------------------------ status store */

let status: FeedStatus = "off";
const statusListeners = new Set<() => void>();

function setStatus(next: FeedStatus): void {
  if (next === status) return;
  status = next;
  for (const l of statusListeners) l();
}

export function useFeedStatus(): FeedStatus {
  return useSyncExternalStore(
    (cb) => {
      statusListeners.add(cb);
      return () => {
        statusListeners.delete(cb);
      };
    },
    () => status,
    () => status,
  );
}

/* ------------------------------------------------------------------ throttled re-read */

const lastRefetch = new Map<string, number>();
const scheduled = new Map<string, ReturnType<typeof setTimeout>>();

/** Re-read one game over REST, trailing-throttled so a burst becomes a single request. */
function refetchContest(contestId: string): void {
  if (scheduled.has(contestId)) return;
  const wait = Math.max(0, (lastRefetch.get(contestId) ?? 0) + REFETCH_WINDOW_MS - Date.now());
  scheduled.set(
    contestId,
    setTimeout(() => {
      scheduled.delete(contestId);
      lastRefetch.set(contestId, Date.now());
      void queryClient.invalidateQueries({ queryKey: ["liquidity", contestId] });
    }, wait),
  );
}

/* ------------------------------------------------------------------ the socket */

interface Frame {
  type?: string;
  contest_id?: string;
  changed_entries?: LiquidityChange[];
  ticks?: Tick[];
  seq?: number;
  refresh_required?: boolean;
  code?: string;
  message?: string;
}

class LiveFeed {
  private ws: WebSocket | null = null;
  private opening = false;
  private attempt = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private wanted: string[] = [];
  private owner: string | null = null;
  private connectedBefore = false;
  /** The channel this socket is subscribed to. */
  private channel: Channel = "liquidity";
  /** The server refused `ticks` for this install — stay on `liquidity` until the user changes. */
  private ticksRefused = false;
  /** Last tick `seq` per game on this connection; a jump means we missed something. */
  private lastSeq = new Map<string, number>();

  constructor() {
    subscribeAuth(() => this.onAuthChange());
  }

  setContests(ids: string[]): void {
    this.wanted = ids.slice(0, MAX_CONTESTS);
    // A game that leaves and later rejoins starts a fresh sequence rather than reading as a gap.
    for (const id of this.lastSeq.keys()) if (!this.wanted.includes(id)) this.lastSeq.delete(id);
    if (this.ws?.readyState === WebSocket.OPEN) this.sendSubscription();
    else if (this.wanted.length) void this.open();
  }

  private onAuthChange(): void {
    if (identity() === this.owner) return; // a silent key refresh — keep the socket
    this.teardown();
    this.ticksRefused = false; // a different install may hold a different plan
    if (this.wanted.length) void this.open();
  }

  private async open(): Promise<void> {
    if (this.ws || this.opening) return;
    if (!hasScope(STREAM_SCOPE)) {
      setStatus("off");
      return;
    }
    this.opening = true;
    setStatus("connecting");
    let key: string | null;
    try {
      key = await getApiKey();
    } catch {
      this.opening = false;
      this.retry(); // network trouble fetching a key — try again shortly
      return;
    }
    this.opening = false;
    if (this.ws) return;
    if (!key) {
      setStatus("off"); // signed out
      return;
    }

    this.owner = identity();
    this.channel = hasScope(TICKS_SCOPE) && !this.ticksRefused ? "ticks" : "liquidity";
    this.lastSeq.clear();
    const ws = new WebSocket(`${STREAM_URL}?api_key=${encodeURIComponent(key)}`);
    this.ws = ws;
    ws.onopen = () => {
      this.attempt = 0;
      this.sendSubscription();
    };
    ws.onmessage = (ev) => this.onFrame(ev.data);
    ws.onclose = () => {
      if (this.ws !== ws) return;
      this.ws = null;
      setStatus("off");
      if (this.wanted.length) this.retry();
    };
    ws.onerror = () => {
      try {
        ws.close();
      } catch {
        // onclose drives the retry either way.
      }
    };
  }

  private onFrame(raw: unknown): void {
    let msg: Frame;
    try {
      msg = JSON.parse(String(raw)) as Frame;
    } catch {
      return;
    }

    if (msg.type === "connected") {
      setStatus("live");
      // Changes only: anything that moved while we were away is recoverable only by re-reading.
      if (this.connectedBefore) void queryClient.invalidateQueries({ queryKey: ["liquidity"] });
      this.connectedBefore = true;
      return;
    }
    if (msg.type === "error") {
      console.warn("[omodds] stream error", msg.code, msg.message);
      // The consent carried `stream:ticks` but the plan doesn't back it: the slower channel still works.
      if (msg.code === "entitlement_required" && this.channel === "ticks") this.fallBackToLiquidity();
      return;
    }
    if (msg.type === "ticks" && msg.contest_id) {
      this.onTicks(msg.contest_id, msg);
      return;
    }
    if (msg.type !== "liquidity.update" || !msg.contest_id) return;

    const contestId = msg.contest_id;
    let unmatched = 0;
    queryClient.setQueryData<Position[]>(["liquidity", contestId], (old) => {
      if (!old) return old;
      const result = applyChanges(old, msg.changed_entries ?? []);
      unmatched = result.unmatched;
      return result.positions;
    });
    // A new venue or line can only be placed by a REST re-read, and so can refresh_required.
    if (msg.refresh_required || unmatched > 0) refetchContest(contestId);
  }

  private onTicks(contestId: string, msg: Frame): void {
    // `seq` counts this game's events; a jump (a dropped or reordered frame) leaves quotes we
    // can't vouch for, so the ticks still apply and a REST re-read backfills the rest.
    let gap = false;
    if (typeof msg.seq === "number") {
      const last = this.lastSeq.get(contestId);
      gap = last !== undefined && msg.seq !== last + 1;
      this.lastSeq.set(contestId, msg.seq);
    }
    let unmatched = 0;
    queryClient.setQueryData<Position[]>(["liquidity", contestId], (old) => {
      if (!old) return old;
      const result = applyTicks(old, msg.ticks ?? []);
      unmatched = result.unmatched;
      return result.positions;
    });
    // A line we don't hold yet can only be placed by a REST re-read.
    if (gap || msg.refresh_required || unmatched > 0) refetchContest(contestId);
  }

  private fallBackToLiquidity(): void {
    this.ticksRefused = true;
    this.channel = "liquidity";
    this.lastSeq.clear();
    this.sendSubscription();
  }

  private sendSubscription(): void {
    const channel = this.channel;
    if (this.wanted.length) this.send({ action: "subscribe", channel, contest_ids: this.wanted });
    else this.send({ action: "unsubscribe", channel });
  }

  private send(obj: unknown): void {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(obj));
  }

  private retry(): void {
    if (this.timer) return;
    const cap = Math.min(30_000, 1_000 * 2 ** this.attempt++);
    setStatus("connecting");
    this.timer = setTimeout(
      () => {
        this.timer = null;
        void this.open();
      },
      cap / 2 + Math.random() * (cap / 2),
    );
  }

  private teardown(): void {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    const ws = this.ws;
    this.ws = null;
    this.owner = null;
    this.attempt = 0;
    this.connectedBefore = false;
    if (ws) {
      try {
        ws.close();
      } catch {
        // Already closed.
      }
    }
    setStatus("off");
  }
}

const feed = typeof window !== "undefined" ? new LiveFeed() : null;

/** Stream the given games while mounted; the set is replaced whenever it changes. */
export function useLiveContests(ids: string[]): void {
  const key = [...new Set(ids)].sort().join(",");
  useEffect(() => {
    feed?.setContests(key ? key.split(",") : []);
  }, [key]);
}
